import type * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The one page shell.
 *
 * Every screen rendered inside `DashboardLayout` / `SettingsLayout` wraps its
 * content in exactly one of these. Geometry comes from the `--page-*` tokens in
 * `design-system/tokens.css`, so the gutter, cap width and vertical rhythm are
 * changed in one place rather than per screen.
 *
 * This replaces five hand-rolled page shells whose max-widths disagreed
 * (1000 / 1120 / 1200 / 1400 / 1440px) — navigating between sibling pages used
 * to shift the content sideways by up to 8px. Do not hand-roll a page shell,
 * and do not add horizontal padding or a max-width on top of this.
 *
 * Children are laid out as a column with `--page-gap` between them, so page
 * blocks (header, filter bar, table, panels) need no margin utilities of their
 * own.
 */
interface PageContainerProps extends React.ComponentProps<"div"> {
  /**
   * "full" drops the `--page-max-w` cap while keeping the same gutters — for the rare
   * screen whose content (a wide table) should use all available width rather than
   * reading as a column down the middle. Default caps at 1440px like every other screen.
   */
  width?: "default" | "full";
}

export function PageContainer({ className, width = "default", ...props }: PageContainerProps) {
  return (
    <div
      data-slot="page-container"
      className={cn(
        "mx-auto flex w-full flex-col gap-[var(--page-gap)]",
        width === "default" && "max-w-[var(--page-max-w)]",
        "px-[var(--page-px)] pt-[var(--page-pt)] pb-[var(--page-pb)]",
        className,
      )}
      {...props}
    />
  );
}
